import Phaser from 'phaser';
import { getAuth, requestPasswordReset } from '../../store/auth.js';
import { Button } from './Button.js';
import { COLORS, FONT_HEADER, FONT_MONO } from '../style.js';
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../PhaserGame.js';

const BACKDROP_ALPHA = 0.78;
const BACKDROP_DEPTH = 1000;
const PANEL_DEPTH = 1001;
const PANEL_WIDTH = 560;
const PANEL_HEIGHT = 480;
const PANEL_RADIUS = 12;

type State = 'ready' | 'working' | 'success' | 'error';

export interface ResetPasswordModalOptions {
  readonly onCancel: () => void;
  /** Called after Supabase accepts the reset request and we've shown
   *  the user the "check your inbox" success state. The modal stays
   *  on screen until they tap Done. */
  readonly onSuccess: () => void;
}

/**
 * Password-reset flow for a signed-in user. We deliberately go through
 * the email-link path (rather than letting the user set a new password
 * inline from Settings) so an attacker who briefly has the user's
 * device can't pivot a momentary session into a permanent account
 * takeover — they'd need inbox access too. Supabase's
 * `auth.resetPasswordForEmail` always returns success even for
 * unknown emails to defeat enumeration; here we know the email
 * because the user is signed in, so success literally means "we
 * asked Supabase to send the email."
 *
 * No backdrop-tap dismiss (matches the ChangeEmailModal fix) so a
 * stray tap near a button doesn't kill the flow.
 */
export class ResetPasswordModal {
  private readonly scene: Phaser.Scene;
  private readonly opts: ResetPasswordModalOptions;

  private readonly objects: Phaser.GameObjects.GameObject[] = [];
  private bodyObjects: Phaser.GameObjects.GameObject[] = [];
  private buttons: Button[] = [];

  private currentState: State = 'ready';
  private errorMsg = '';
  private destroyed = false;

  constructor(scene: Phaser.Scene, opts: ResetPasswordModalOptions) {
    this.scene = scene;
    this.opts = opts;

    this.drawBackdrop();
    this.drawPanel();
    this.renderBody();

    this.scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.destroy();
    });
  }

  // --- Chrome ---

  private drawBackdrop(): void {
    const bg = this.scene.add
      .rectangle(
        DESIGN_WIDTH / 2,
        DESIGN_HEIGHT / 2,
        DESIGN_WIDTH,
        DESIGN_HEIGHT,
        0x000000,
        BACKDROP_ALPHA
      )
      .setDepth(BACKDROP_DEPTH)
      .setInteractive();
    // No backdrop-tap cancel — Cancel button is the only dismiss
    // path. setInteractive() stays so taps don't leak through to
    // SettingsScene buttons underneath.
    this.objects.push(bg);
  }

  private drawPanel(): void {
    const left = DESIGN_WIDTH / 2 - PANEL_WIDTH / 2;
    const top = DESIGN_HEIGHT / 2 - PANEL_HEIGHT / 2;

    const panel = this.scene.add.graphics().setDepth(PANEL_DEPTH);
    panel.fillStyle(COLORS.bgDeepInt, 1);
    panel.fillRoundedRect(left, top, PANEL_WIDTH, PANEL_HEIGHT, PANEL_RADIUS);
    panel.lineStyle(2, COLORS.accentCyanInt, 0.95);
    panel.strokeRoundedRect(left, top, PANEL_WIDTH, PANEL_HEIGHT, PANEL_RADIUS);
    this.objects.push(panel);
  }

  // --- State-driven body ---

  private renderBody(): void {
    for (const o of this.bodyObjects) o.destroy();
    this.bodyObjects = [];
    for (const b of this.buttons) b.destroy();
    this.buttons = [];

    switch (this.currentState) {
      case 'ready':
        this.renderReady();
        break;
      case 'working':
        this.renderWorking();
        break;
      case 'success':
        this.renderSuccess();
        break;
      case 'error':
        this.renderError();
        break;
    }
  }

  private renderReady(): void {
    const cx = DESIGN_WIDTH / 2;
    const top = DESIGN_HEIGHT / 2 - PANEL_HEIGHT / 2;

    this.addTitle('Reset password', top + 36, COLORS.accentCyanHex);

    const auth = getAuth();
    const email = auth.user?.email ?? '(unknown)';

    this.addBody(
      `Forgot your password? We'll email a reset link to ${email}. Click the link to set a new one.`,
      top + 120,
      COLORS.textPrimary,
      'left'
    );

    this.addBody(
      'For security, this flow goes through your email even though you’re signed in.',
      top + 240,
      COLORS.textSecondary,
      'left'
    );

    this.addCancelButton(top + PANEL_HEIGHT - 56, cx - 130);
    this.addSubmitButton(top + PANEL_HEIGHT - 56, cx + 130);
  }

  private renderWorking(): void {
    const top = DESIGN_HEIGHT / 2 - PANEL_HEIGHT / 2;
    this.addTitle('Sending reset link…', top + 36, COLORS.accentCyanHex);
    this.addBody(
      'Asking the server to send the reset link to your email.',
      top + 200,
      COLORS.textMuted
    );
    // No buttons — short-lived state.
  }

  private renderSuccess(): void {
    const top = DESIGN_HEIGHT / 2 - PANEL_HEIGHT / 2;
    const cx = DESIGN_WIDTH / 2;

    const auth = getAuth();
    const email = auth.user?.email ?? '(unknown)';

    this.addTitle('Check your inbox', top + 36, COLORS.accentCyanHex);
    this.addBody(
      `We sent a reset link to ${email}.`,
      top + 120,
      COLORS.textPrimary
    );
    this.addBody(
      'Click the link in that email to set a new password. The link expires in 1 hour.',
      top + 200,
      COLORS.textSecondary,
      'left'
    );

    const close = new Button(this.scene, {
      x: cx,
      y: top + PANEL_HEIGHT - 56,
      width: 220,
      height: 64,
      text: 'Done',
      style: 'primary',
      onTap: () => {
        this.opts.onSuccess();
        this.destroy();
      },
    });
    close.setDepth(PANEL_DEPTH + 2);
    this.buttons.push(close);
  }

  private renderError(): void {
    const top = DESIGN_HEIGHT / 2 - PANEL_HEIGHT / 2;
    const cx = DESIGN_WIDTH / 2;

    this.addTitle("Couldn't send reset link", top + 36, COLORS.accentRedHex);
    this.addBody(this.errorMsg, top + 140, COLORS.textPrimary, 'left');

    this.addCancelButton(top + PANEL_HEIGHT - 56, cx - 130);
    const retry = new Button(this.scene, {
      x: cx + 130,
      y: top + PANEL_HEIGHT - 56,
      width: 220,
      height: 64,
      text: 'Try again',
      style: 'primary',
      onTap: () => {
        this.currentState = 'ready';
        this.renderBody();
      },
    });
    retry.setDepth(PANEL_DEPTH + 2);
    this.buttons.push(retry);
  }

  // --- Logic ---

  private async handleSubmit(): Promise<void> {
    const auth = getAuth();
    const email = auth.user?.email;
    if (!email) {
      this.errorMsg = 'No email on the current session.';
      this.currentState = 'error';
      this.renderBody();
      return;
    }

    this.currentState = 'working';
    this.renderBody();

    try {
      const result = await requestPasswordReset(email);
      if (this.destroyed) return;
      if (!result.ok) {
        this.errorMsg = result.error;
        this.currentState = 'error';
        this.renderBody();
        return;
      }
      this.currentState = 'success';
      this.renderBody();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      this.errorMsg = `Network error: ${msg}`;
      this.currentState = 'error';
      this.renderBody();
    }
  }

  private handleCancel(): void {
    if (this.currentState === 'working') return;
    this.opts.onCancel();
    this.destroy();
  }

  // --- Body helpers ---

  private addTitle(text: string, y: number, color: string): void {
    const t = this.scene.add
      .text(DESIGN_WIDTH / 2, y, text, {
        fontFamily: FONT_HEADER,
        fontSize: '26px',
        color,
        fontStyle: '700',
      })
      .setLetterSpacing(2)
      .setOrigin(0.5, 0)
      .setDepth(PANEL_DEPTH + 1);
    this.bodyObjects.push(t);
  }

  private addBody(
    text: string,
    y: number,
    color: string,
    align: 'center' | 'left' = 'center'
  ): void {
    const t = this.scene.add
      .text(DESIGN_WIDTH / 2, y, text, {
        fontFamily: FONT_MONO,
        fontSize: '15px',
        color,
        align,
        wordWrap: { width: PANEL_WIDTH - 80 },
      })
      .setLineSpacing(4)
      .setOrigin(0.5, 0)
      .setDepth(PANEL_DEPTH + 1);
    this.bodyObjects.push(t);
  }

  private addCancelButton(y: number, x: number): void {
    const btn = new Button(this.scene, {
      x,
      y,
      width: 220,
      height: 64,
      text: 'Cancel',
      onTap: () => this.handleCancel(),
    });
    btn.setDepth(PANEL_DEPTH + 2);
    this.buttons.push(btn);
  }

  private addSubmitButton(y: number, x: number): void {
    const btn = new Button(this.scene, {
      x,
      y,
      width: 220,
      height: 64,
      text: 'Send link',
      style: 'primary',
      onTap: () => void this.handleSubmit(),
    });
    btn.setDepth(PANEL_DEPTH + 2);
    this.buttons.push(btn);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    for (const o of this.objects) o.destroy();
    this.objects.length = 0;
    for (const o of this.bodyObjects) o.destroy();
    this.bodyObjects = [];
    for (const b of this.buttons) b.destroy();
    this.buttons = [];
  }
}
